import { v4 as uuidv4 } from 'uuid';

enum MessageType {
  // Client-bound / Incoming
  WELCOME = 0,
  CALLRESULT = 3,
  CALLERROR = 4,
  EVENT = 8,
  // Server-bound / Outgoing
  SET_PREFIX = 1,
  CALL = 2,
  SUBSCRIBE_TOPIC = 5,
  UNSUBSCRIBE_TOPIC = 6,
}

/**
 * A topic prefix, is basically a shorthand for a TopicURI
 */
type TopicPrefix = string;
/**
 * A TopicURI is a URI for namespacing topics
 */
type TopicURI = string;
/**
 * A Topic is a named channel that can receive messages
 */
type Topic = string;
/**
 * The SessionId is set by the server on each connection
 */
type SessionId = number;
type Version = number; // Not quite sure about this one
type ServerName = string;
type CallId = string;
type ProcedureURI = string;

type UnknownMessage = Readonly<[MessageType, ...unknown[]]>;
type WelcomeMessage = [MessageType.WELCOME, SessionId, Version, ServerName];
type EventMessage<Data = unknown> = [MessageType.EVENT, Topic, Data];
type CallMessage = Readonly<
  [MessageType.CALL, CallId, ProcedureURI, ...unknown[]]
>;
type CallResultMessage<Result = unknown> = [
  MessageType.CALLRESULT,
  CallId,
  Result,
];
type CallErrorMessage = [
  MessageType.CALLERROR,
  CallId,
  errorUri: string,
  errorDescription: string,
  errorDetails?: unknown,
];

const createPrefixMessage = (prefix: string, uri: string) =>
  [MessageType.SET_PREFIX, prefix, uri] as const;

const createSubscribeMessage = (prefix: string, topic: string) =>
  [MessageType.SUBSCRIBE_TOPIC, `${prefix}:${topic}`] as const;

const createUnsubscribeMessage = (prefix: string, topic: string) =>
  [MessageType.UNSUBSCRIBE_TOPIC, `${prefix}:${topic}`] as const;

const createCallMessage = (
  callId: CallId,
  procedureUri: ProcedureURI,
  params: readonly unknown[]
): CallMessage => [MessageType.CALL, callId, procedureUri, ...params];

class ResubscribeError extends Error {
  constructor() {
    super('Cannot resubscribe to topic');
  }
}

export class NotConnectedError extends Error {
  constructor() {
    super('Cannot call procedure while not connected');
    this.name = 'NotConnectedError';
  }
}

export class WAMPCallError extends Error {
  readonly errorUri: string;
  readonly details?: unknown;

  constructor(errorUri: string, description: string, details?: unknown) {
    super(description);
    this.name = 'WAMPCallError';
    this.errorUri = errorUri;
    this.details = details;
  }
}

export class ConnectionClosedError extends Error {
  constructor() {
    super('Connection closed before a call result was received');
    this.name = 'ConnectionClosedError';
  }
}

export class CallTimedOutError extends Error {
  constructor() {
    super('Procedure call timed out');
    this.name = 'CallTimedOutError';
  }
}

export const isWAMPConnectionError = (error: unknown): boolean =>
  error instanceof NotConnectedError ||
  error instanceof ConnectionClosedError ||
  error instanceof CallTimedOutError;

type PendingCall = {
  resolve: (result: unknown) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

export enum ConnectionStatus {
  CREATED,
  CONNECTING,
  CONNECTED,
  RECONNECTING,
  DEAD,
}

const RECONNECT_INTERVAL = 3000;
const MAX_RETRIES = 10;
const CALL_TIMEOUT = 30_000;

/**
 * SimpleWAMP is a very simple Websocket wrapper, that tries to be the smallest
 * possible implementation of the subset of WAMP that Ardoq uses.
 *
 * As a simplification, topic URIs are ignored, so only the topic itself is
 * considered. The topics must still be registered to the server.
 *
 * In the context of Ardoq, the job of SimpleWAMP is to speak the WAMP protocol
 * and allow subscriptions to events. SimpleWAMP enforces a maximum of one
 * subscriber per event. If the connection is broken, reconnection is
 * automatically handled and events are resubscribed.
 */
export class SimpleWAMP {
  status = ConnectionStatus.CREATED;

  prefix?: [TopicPrefix, TopicURI];
  subscriptions = new Map<Topic, Set<(event: any) => void | Promise<void>>>();
  pendingCalls = new Map<CallId, PendingCall>();

  uri: string;

  websocket?: WebSocket;
  sessionId?: number;
  retries = 0;

  connectedCallback?: (wamp: this) => void | Promise<void>;
  reconnectingCallback?: (wamp: this, event: CloseEvent) => void;
  disconnectedCallback?: (wamp: this) => void;
  errorCallback?: (wamp: this, event: Event) => void;

  constructor(uri: string) {
    this.uri = uri;
  }

  connect() {
    if (
      this.status !== ConnectionStatus.CREATED &&
      this.status !== ConnectionStatus.DEAD
    ) {
      return;
    }

    this.status = ConnectionStatus.CONNECTING;
    this.connectWs();
  }

  close() {
    this.status = ConnectionStatus.DEAD;
    this.websocket?.close();
    this.rejectPendingCalls(new ConnectionClosedError());
    this.disconnectedCallback?.(this);
  }

  private connectWs() {
    if (
      this.status !== ConnectionStatus.CONNECTING &&
      this.status !== ConnectionStatus.RECONNECTING
    ) {
      return;
    }

    this.websocket?.close();
    this.websocket = new WebSocket(this.uri, 'wamp');
    this.websocket.onclose = this.handleClose.bind(this);
    this.websocket.onmessage = this.handleMessage.bind(this);
    this.websocket.onopen = this.handleOpen.bind(this);
    this.websocket.onerror = e => this.errorCallback?.(this, e);
  }

  private handleClose(event: CloseEvent) {
    if (this.status === ConnectionStatus.DEAD) {
      return;
    }
    this.status = ConnectionStatus.RECONNECTING;
    this.rejectPendingCalls(new ConnectionClosedError());
    this.reconnectingCallback?.(this, event);
    this.reconnect();
  }

  private rejectPendingCalls(error: Error) {
    for (const pendingCall of this.pendingCalls.values()) {
      clearTimeout(pendingCall.timeoutId);
      pendingCall.reject(error);
    }
    this.pendingCalls.clear();
  }

  private reconnect() {
    if (this.retries >= MAX_RETRIES) {
      return this.close();
    }
    this.retries++;

    setTimeout(this.connectWs.bind(this), RECONNECT_INTERVAL);
  }

  private handleOpen(_event: Event) {
    this.retries = 0;
  }

  private async handleWelcome([_, sessionId, __, ___]: WelcomeMessage) {
    this.sessionId = sessionId;
    this.status = ConnectionStatus.CONNECTED;

    if (this.prefix) {
      this.send(createPrefixMessage(...this.prefix));
      for (const topic of this.subscriptions.keys()) {
        this.send(createSubscribeMessage(this.prefix[0], topic));
      }
    }

    await this.connectedCallback?.(this);
  }

  private async handleEvent<Event>([_, longTopic, event]: EventMessage<Event>) {
    const topicParts = longTopic.split('#');
    const shortTopic = topicParts[topicParts.length - 1];

    const callbacks = this.subscriptions.get(shortTopic);
    if (callbacks) {
      await Promise.allSettled(
        Array.from(callbacks).map(callback => callback(event))
      );
    }
  }

  private handleCallResult([_, callId, result]: CallResultMessage) {
    const pendingCall = this.pendingCalls.get(callId);
    if (!pendingCall) {
      return;
    }

    this.pendingCalls.delete(callId);
    clearTimeout(pendingCall.timeoutId);
    pendingCall.resolve(result);
  }

  private handleCallError([
    _,
    callId,
    errorUri,
    description,
    details,
  ]: CallErrorMessage) {
    const pendingCall = this.pendingCalls.get(callId);
    if (!pendingCall) {
      return;
    }

    this.pendingCalls.delete(callId);
    clearTimeout(pendingCall.timeoutId);
    pendingCall.reject(new WAMPCallError(errorUri, description, details));
  }

  private async handleMessage(event: MessageEvent<any>) {
    const message = JSON.parse(event.data) as UnknownMessage;
    switch (message[0]) {
      case MessageType.WELCOME:
        return await this.handleWelcome(message as WelcomeMessage);
      case MessageType.EVENT:
        return await this.handleEvent(message as EventMessage);
      case MessageType.CALLRESULT:
        return this.handleCallResult(message as CallResultMessage);
      case MessageType.CALLERROR:
        return this.handleCallError(message as CallErrorMessage);
      default:
        this.errorCallback?.(
          this,
          new CustomEvent('unknown-message', {
            detail: { type: message[0], message },
          })
        );
    }
  }

  private send(message: UnknownMessage) {
    if (this.websocket?.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(message));
    }
  }

  setPrefix(prefix: TopicPrefix, uri: TopicURI) {
    this.prefix = [prefix, uri];

    this.send(createPrefixMessage(prefix, uri));
  }

  subscribe<Event>(
    prefix: TopicPrefix,
    topic: Topic,
    callback: (event: Event) => void
  ) {
    if (prefix !== this.prefix?.[0]) {
      throw new Error('Cannot subscribe to event with other prefix');
    }

    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
      this.send(createSubscribeMessage(prefix, topic));
    }

    const callbacks = this.subscriptions.get(topic)!;

    if (callbacks.has(callback)) {
      throw new ResubscribeError();
    }

    callbacks.add(callback);
  }

  unsubscribe(
    prefix: TopicPrefix,
    topic: Topic,
    callback: (event: any) => void
  ) {
    const callbacks = this.subscriptions.get(topic);
    if (!callbacks) {
      return;
    }

    callbacks.delete(callback);

    // If no more callbacks for this topic, remove the topic entirely
    if (callbacks.size === 0) {
      this.subscriptions.delete(topic);
      this.send(createUnsubscribeMessage(prefix, topic));
    }
  }

  call<Result>(
    procedureUri: ProcedureURI,
    ...params: readonly unknown[]
  ): Promise<Result> {
    if (
      this.status !== ConnectionStatus.CONNECTED ||
      this.websocket?.readyState !== WebSocket.OPEN
    ) {
      return Promise.reject(new NotConnectedError());
    }

    const callId = uuidv4();
    return new Promise<Result>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingCalls.delete(callId);
        reject(new CallTimedOutError());
      }, CALL_TIMEOUT);
      this.pendingCalls.set(callId, {
        resolve: result => resolve(result as Result),
        reject,
        timeoutId,
      });
      try {
        this.send(createCallMessage(callId, procedureUri, params));
      } catch (error) {
        this.pendingCalls.delete(callId);
        clearTimeout(timeoutId);
        throw error;
      }
    });
  }

  /**
   * Callback for when the connection is successfully established.
   * Will also be called when the socket is reconnected.
   */
  onConnected(callback: (wamp: this) => void | Promise<void>) {
    this.connectedCallback = callback;
  }

  /**
   * Callback for when the connection is dropped, and we start reconnection
   * attempts.
   */
  onReconnecting(callback: (wamp: this, event: CloseEvent) => void) {
    this.reconnectingCallback = callback;
  }

  /**
   * Callback for when the socket is disconnected and no longer tries to
   * reconnect.
   * Called when max number of retries is reached for reconnection, or when
   * `.close()` is called.
   */
  onDisconnected(callback: (wamp: this) => void) {
    this.disconnectedCallback = callback;
  }

  /**
   * Callback for Websocket errors. This is typically when the client cannot
   * establish a connection.
   */
  onError(callback: (wamp: this, event: Event) => void) {
    this.errorCallback = callback;
  }
}
